// Authentication API functions

import { apiPost, apiGet, ApiClientError, API_BASE_URL } from '../api-client'
import type { LoginRequest, SignupRequest, User } from '../types/api'

export interface MeByCookieResponse {
  user: User
  accessToken?: string
  refreshToken?: string
  expiresIn?: number
}

export interface LoginResponse {
  user: User
  accessToken: string
  idToken?: string
  refreshToken?: string
  expiresIn?: number
  message?: string
}

export interface SignupResponse {
  user: User
  message?: string
}

export async function login(email: string, password: string): Promise<LoginResponse> {
  const request: LoginRequest = { email, password }
  return apiPost<LoginResponse>('/auth/signin', request)
}

export async function signup(name: string, email: string, password: string): Promise<SignupResponse> {
  const request: SignupRequest = { name, email, password }
  return apiPost<SignupResponse>('/auth/signup', request)
}

export async function logout(): Promise<void> {
  try {
    await apiPost('/auth/signout')
  } catch (error) {
    console.error('Logout error:', error)
  }
}

export async function loginWithGoogle(googleIdToken: string): Promise<LoginResponse> {
  return apiPost<LoginResponse>('/auth/google', { googleIdToken })
}

export async function getCurrentUser(): Promise<User | null> {
  try {
    const response = await apiGet<{ user: User }>('/auth/me')
    return response.user || null
  } catch (error) {
    // Treat 401 as "not authenticated" without surfacing an error to the UX
    if (error instanceof ApiClientError && error.status === 401) {
      return null
    }
    // For other errors (network issues, 5xx, etc.), just log in dev and
    // fall back to "not authenticated" so the marketing site never breaks.
    if (import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.error('getCurrentUser failed:', error)
    }
    return null
  }
}

/** Call GET /auth/me-by-cookie with credentials (no Bearer) so shared session cookie is used. */
export async function getCurrentUserByCookie(): Promise<MeByCookieResponse | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/me-by-cookie`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
    if (!response.ok) return null
    const data = (await response.json()) as MeByCookieResponse
    if (!data?.user) return null
    return data
  } catch {
    return null
  }
}

